import React, { useRef, useState } from 'react';

type AvatarUploaderProps = {
  currentPhotoUrl?: string | null;
  onUpload: (file: File) => Promise<void>;
  disabled?: boolean;
};

export const AvatarUploader: React.FC<AvatarUploaderProps> = ({ currentPhotoUrl, onUpload, disabled }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      await onUpload(file);
    } catch (err) {
      console.error('Failed to upload avatar', err);
      alert('Failed to upload image. Must be an image file under 5MB.');
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        data-testid="avatar-uploader"
        className={`relative h-40 w-40 overflow-hidden rounded-2xl border-2 border-dashed flex items-center justify-center cursor-pointer transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed border-slate-300' :
          isHovering ? 'border-primary-500 bg-primary-50' : 'border-slate-300 bg-slate-50'
        }`}
        onMouseEnter={() => !disabled && setIsHovering(true)}
        onMouseLeave={() => !disabled && setIsHovering(false)}
        onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
      >
        {isUploading ? (
          <div className="text-sm text-slate-500 animate-pulse font-medium">Uploading...</div>
        ) : currentPhotoUrl ? (
          <img src={currentPhotoUrl} alt="Avatar" className="w-full h-full object-cover" />
        ) : (
          <div className="text-slate-400 flex flex-col items-center">
            <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-[10px] font-medium text-center leading-tight">Upload<br/>Photo</span>
          </div>
        )}

        {isHovering && currentPhotoUrl && !isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
             <span className="text-white text-xs font-medium">Change</span>
          </div>
        )}
      </div>
      
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
        disabled={disabled || isUploading}
      />
    </div>
  );
};
